import { Component, Input, OnInit, ViewChild, Pipe, PipeTransform, AfterViewInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Config, IonAlert, IonModal, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * CancelableLoading Dialog Component.
 *
 * This component implements a cancelable loading dialog that can be used in two distinct ways:
 *
 * ### Inline usage in HTML (Recommended):
 *    The CancelableLoading dialog is embedded directly in an HTML template:
 *
 *    Example for inline usage:
 *
 *    ```html
 *    <cancelable-loading 
 *       [isOpen]="loadingIsOpen"
 *       [cancelFn]="onCancel"
 *       [cancelableAfterMillis]="3000"
 *       [confirmCancellation]="true"
 *       [cnfHeader]="'JUST_A_EXAMPLE.CANCEL_DOWNLOAD_CONFIRMATION_HEADER'"
 *       [translateCnfHeader]="true"
 *       [loadingMsg]="'LOADING.DATA'">
 *    </cancelable-loading>
 *    ```
 *
 * ### Non-inline usage via direct TypeScript call:
 *    The dialog can also be presented programmatically through the `AlertService`.
 *    The `AlertService` defines the following key methods:
 *
 *    - `presentLoading`: Presents the standard loading modal:
 *      ```typescript
 *      await alertService.presentLoading({
 *        cancelFn: () => { console.log('Loading cancelled'); },
 *        cancelableAfterMillis: 3000,
 *        confirmCancellation: true,
 *        cnfHeader: 'CONFIRMATION.HEADER',
 *        loadingMsg: 'LOADING.DATA'
 *      });
 *      ```
 *
 *    - `dismissLoading`: Dismisses the loading modal:
 *      ```typescript
 *      await alertService.dismissLoading();
 *      ```
 */
@Component({
    selector: 'cancelable-loading',
    templateUrl: './cancelable-loading.component.html',
    styleUrls: ['./cancelable-loading.component.md.scss', 'cancelable-loading.component.ios.scss'],
    standalone: false
})
export class CancelableLoading implements OnDestroy, OnInit, AfterViewInit {
  /**
   * Callback function triggered when the user presses the cancel button.
   * Please note that setting a callback function is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() cancelFn?: () => void;

  /**
   * Controls whether the modal is open.
   * This attribute is only relevant when the `CancelableLoding` component is used in inline mode.
   * The default value is `false`.
   */
  @Input() isOpen: boolean = false;

  /**
   * Hides the cancel button of the loading dialog.
   * By default, this attribute will be set to `false`.
   * Please note that setting the attributes `cancelFn`, `cancelableAfterMillis`, `confirmCancellation`, `cnfHeader`, `translateCnfHeader`, `cnfSubHeader`, `translateCnfSubHeader`, `cnfMsg` or `translateCnfMsg` is useless if you set this attribute to `true`!
   */
  @Input() cancelIsDeactivated: boolean = false;

  /**
   * Time in milliseconds after which the cancel button becomes visible.
   * If not specified the cancel button will be visible immediately in the loading dialog.
   * Please note that setting timeout is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() cancelableAfterMillis?: number;

  /**
   * Whether to show a confirmation dialog before canceling.
   * The default value is `false`.
   * Please note that setting a cancellation confirmation is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() confirmCancellation: boolean = false;

  /**
   * Header text for the cancel confirmation dialog that is used in the `ion-alert` component.
   * For more information about the `ion-alert` component see: https://ionicframework.com/docs/api/alert
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateCnfHeader` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateCnfHeader` will be translated and concatenated with the remaining strings.
   * If no value is specified, the i18n translation key `"GENERAL.SURE_CANCEL"` will be used as the default value.
   * Please note that setting a confirmation header is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() cnfHeader?: string[] | string = "GENERAL.SURE_CANCEL";

  /**
   * Indicates whether the cancel confirmation header or specific parts of it should be translated.
   * If `cnfHeader` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `cnfHeader` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * By default, this attribute will be set to `true` during initialization if `cnfHeader` is `"GENERAL.SURE_CANCEL"` and no explicit value was provided (`undefined`).
   * Please note that setting a confirmation header translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() translateCnfHeader?: number[] | boolean;

  /**
   * Subheader text for the cancel confirmation dialog that is used in the `ion-alert` component.
   * For more information about the `ion-alert` component see: https://ionicframework.com/docs/api/alert
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateCnfSubHeader` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateCnfSubHeader` will be translated and concatenated with the remaining strings.
   * Please note that setting a confirmation subheader is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() cnfSubHeader?: string[] | string;

  /**
   * Indicates whether the cancel confirmation subheader or specific parts of it should be translated.
   * If `cnfSubHeader` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `cnfSubHeader` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * Please note that setting a confirmation subheader translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() translateCnfSubHeader?: number[] | boolean;

  /**
   * Message text for the cancel confirmation dialog that is used in the `ion-alert` component.
   * For more information about the `ion-alert` component see: https://ionicframework.com/docs/api/alert
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateCnfMsg` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateCnfMsg` will be translated and concatenated with the remaining strings.
   * Please note that setting a confirmation message is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() cnfMsg?: string[] | string;

  /**
   * Indicates whether the cancel confirmation message or specific parts of it should be translated.
   * If `cnfMsg` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `cnfMsg` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * Please note that setting a confirmation message translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() translateCnfMsg?: number[] | boolean;

  /**
   * Message text for the loading dialog.
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateLoadingMsg` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateLoadingMsg` will be translated and concatenated with the remaining strings.
   * Please note that setting a confirmation message translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  @Input() loadingMsg?: string[] | string = "GENERAL.PLEASE_WAIT";

  /**
   * Indicates whether the loading message or specific parts of it should be translated.
   * If `loadingMsg` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `loadingMsg` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * By default, this attribute will be set to `true` during initialization if `loadingMsg` is `"GENERAL.PLEASE_WAIT"` and no explicit value was provided (`undefined`).
   */
  @Input() translateLoadingMsg?: number[] | boolean;

  /** Configuration of no and yes buttons in the confirmation dialog */
  cnfButtons = [
    {
      text: this.translateService.instant("BUTTONS.NO"),
      role: "cancel",
    },
    {
      text: this.translateService.instant("BUTTONS.YES"),
      role: "confirm",
      handler: () => {
        this.cancel();
      },
    },
  ];

  /** Reference to the confirmation dialog component */
  @ViewChild("cnfDialogRef") private cnfDialog: IonAlert;

  /** Controls the visibility of the cancel button */
  cancelButtonVisible: boolean = false;

  /** Determines if the component should be displayed inline or as a modal. */
  inlineMode: boolean = true;

  /** Spinner type based on the application mode (iOS style or Material Design (Android and Web)) */
  spinner: string;

  /**
   * Unique ID of the inline or non-inline modal of the `ion-modal` component.
   * If this component is not used in inline mode, the ID of the parent element will be injected by the `AlertService`.
   * Otherwise, this component will assign a random ID during initialization.
   */
  id: string;

  private subscriptions: Subscription[] = [];

  constructor(
    private config: Config,
    private modalController: ModalController,
    private translateService: TranslateService,
  ) {
    this.spinner = this.config.get("mode") === "md" ? "crescent" : "lines";
    this.id = `cancelable-loading-${CancelableLoading.randomHexString(8)}`;

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe(() => {
        for (const button of this.cnfButtons) {
          if (button.role === "cancel") {
            button.text = this.translateService.instant("BUTTONS.NO");
          } else if (button.role === "confirm") {
            button.text = this.translateService.instant("BUTTONS.YES");
          }
        }
      })
    );
  }

  ngAfterViewInit(): void {
    if (!this.inlineMode) {
      const ionModal = document.getElementById(this.id) as unknown as IonModal;
      ionModal?.onWillDismiss().then(() => this.cnfDialog.dismiss());
    }
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Initializes default translation texts and sets the visibility of the cancel button based on the provided inputs.
   */
  ngOnInit(): void {
    this.initDefaultText();
    if (!this.inlineMode) {
      this.setCancelButtonVisibility();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handles the cancel button click event.
   * Shows a confirmation dialog if required, otherwise cancels directly.
   */
  cancelButtonClicked(): void {
    if (this.confirmCancellation) {
      this.makeButtonTabFocusable(false);
      this.cnfDialog.present();
    } else {
      this.cancel();
    }
  }

  /**
   * Initializes default translation settings for the confirmation header and loading message
   * if they are set to their default keys and no explicit translation flags were provided.
   */
  private initDefaultText(): void {
    if (this.cnfHeader === "GENERAL.SURE_CANCEL" && typeof this.translateCnfHeader === "undefined") {
      this.translateCnfHeader = true;
    }

    if (this.loadingMsg === "GENERAL.PLEASE_WAIT" && typeof this.translateLoadingMsg === "undefined") {
      this.translateLoadingMsg = true;
    }
  }

  /**
   * Cancels the loading dialog and triggers the onCancel callback if provided.
   */
  private cancel() {
    this.cancelFn?.();
    this.modalController.dismiss();
  }

  /**
   * Sets the cancel button's tabindex attribute to control focus behavior.
   * @param focusable - Whether the button should be focusable.
   */
  private makeButtonTabFocusable(focusable: boolean): void {
    const button = document.querySelector('.cancel-button') as HTMLButtonElement;
    if (!button) {
      return;
    }

    button.setAttribute("tabindex", focusable ? "0" : "-1");
    button.blur();
  }


  /**
   * Sets a timeout to display the cancel button after a specified number of milliseconds.
   * If `cancelableAfterMillis` is defined and `cancelIsDeactivated` is false, the cancel button
   * will become visible after the specified timeout. If `cancelIsDeactivated` is false but
   * `cancelableAfterMillis` is not defined, the cancel button will become visible immediately.
   */
  setCancelButtonVisibility(): void {
    if (this.cancelableAfterMillis && !this.cancelIsDeactivated) {
      setTimeout(
        () => {
          this.cancelButtonVisible = true;
        },
        this.cancelableAfterMillis,
      );
    } else if (!this.cancelIsDeactivated) {
      this.cancelButtonVisible = true;
    }
  }

  /**
     * Handles the event when the modal is dismissed.
     * Resets the visibility of the cancel button if the `cancelableAfterMillis` attribute is defined
     * and the `cancelIsDeactivated` attribute is set to `false`.
     */
  modalDidDismiss(): void {
    if (this.cancelableAfterMillis && !this.cancelIsDeactivated) {
      this.cancelButtonVisible = false;
    }
    this.cnfDialog?.dismiss();
  }

  /**
   * Generates a (non cryptographic) random hexadecimal string of the specified size.
   *
   * @param size - The length of the resulting hexadecimal string.
   * @returns A random hexadecimal string of the specified length.
   */
  static randomHexString(size: number): string {
    return [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
  }
}

/**
 * Pipe for translating and concatenating text based on an input array or string.
 */
@Pipe({
    name: 'translateText',
    pure: false,
    standalone: false
})
export class ConcatTranslationPipe implements PipeTransform, OnDestroy {
  private langChangeSub: Subscription;
  private lastInput: string[] | string | undefined;
  private lastTranslate: number[] | boolean | undefined;
  private lastResult: string | undefined;

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.lastResult = undefined;
      this.cdr.markForCheck();
    });
  }

  /**
   * Transforms the input text by translating specified elements.
   * @param input - The input text or array of text segments.
   * @param translate - Boolean or index array specifying which elements to translate.
   * @returns The translated and concatenated string.
   */
  transform(input: string[] | string, translate: number[] | boolean): string | undefined {
    if (
      input === this.lastInput &&
      translate === this.lastTranslate &&
      this.lastResult !== undefined
    ) {
      return this.lastResult;
    }

    this.lastInput = input;
    this.lastTranslate = translate;

    if (typeof input === "undefined" || input === null) {
      return undefined;
    }

    if (typeof input === "string" && typeof translate === "boolean") {
      this.lastResult = translate
        ? this.translateService.instant(input)
        : input;
      return this.lastResult;
    }

    if (Array.isArray(input) && Array.isArray(translate)) {
      let result = "";
      input.forEach((value, index) => {
        result += translate.includes(index)
          ? this.translateService.instant(value)
          : value;
      });
      this.lastResult = result;
      return result;
    }

    if (Array.isArray(input)) {
      this.lastResult = input.join("");
      return this.lastResult;
    }

    if (typeof input === "string") {
      this.lastResult = input;
      return input;
    }
  }

  ngOnDestroy(): void {
    this.langChangeSub.unsubscribe();
  }
}
